import React, { useState, useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import ls from 'local-storage';
import { pd, pluralize } from 'utils';
import Icon from 'components/common/Icon';

const FILTER_ALL = 'All';
const FILTER_ADDED = 'Added';
const FILTER_NOT_ADDED = 'Not added';
const FILTER_CHOICES = [FILTER_ALL, FILTER_ADDED, FILTER_NOT_ADDED];

const ReleaseContentMenu = ({ itemsById, releaseItems, onItemAdd }) => {
  const includedReleaseItems = Object.values(itemsById).filter(({ target }) => !!target).map(({ target }) => target.uid);

  const [itemFilter, setItemFilter] = useState(ls.get('releaseNav-itemFilter') || FILTER_ALL);
  const handleItemFilterChange = evt => {
    evt.preventDefault();
    const val = decodeURIComponent(evt.target.hash.replace('#', ''));
    setItemFilter(val);
    ls.set('releaseNav-itemFilter', val);
  };

  const [itemFilterShowAll, setItemFilterShowAll] = useState(false);
  useEffect(() => {
    setItemFilterShowAll(false);
  }, [itemFilter]);

  const releaseContentListItems = useMemo(() => {
    return {
      [FILTER_ALL]: releaseItems,
      [FILTER_ADDED]: releaseItems.filter(({ uid }) => includedReleaseItems.includes(uid)),
      [FILTER_NOT_ADDED]: releaseItems.filter(({ uid }) => !includedReleaseItems.includes(uid)),
    };
  }, [releaseItems, JSON.stringify(includedReleaseItems)]);

  let showAllLink;
  let visibleReleaseItems = releaseContentListItems[itemFilter];
  if (!itemFilterShowAll && visibleReleaseItems.length > 10) {
    const extraCount = visibleReleaseItems.length - 10;
    showAllLink = (
      <a href="#show-all" onClick={pd(() => setItemFilterShowAll(true))}>
        Show {extraCount} more {pluralize(extraCount, 'item', null, true)}…
      </a>
    );
    visibleReleaseItems = visibleReleaseItems.slice(0, 10);
  }

  return (
    <>
      <div className="form-group mb-4">
        <label className="block-label strong">Release Content</label>
        <div className="form-input-hint mt-0 mb-1">Link to items within this release. If you have unsaved changes, you may need to save to refresh this list.</div>
      </div>

      <div className="mb-2">
        <span>Filter items: &nbsp;</span>
        {FILTER_CHOICES.map((c, idx, arr) => (
          <Fragment key={c}>
            {itemFilter === c
              ? <strong>{c}</strong>
              : <a href={`#${c}`} onClick={handleItemFilterChange}>{c}</a>}
            {idx < (arr.length - 1) && <span className="text-meta"> &middot; </span>}
          </Fragment>
        ))}
      </div>

      <div className="mb-6">
        {visibleReleaseItems.map(({ uid, objectName, objectIconName, objectType, sectionName }, idx) => (
          <Fragment key={uid}>
            <hr />
            <div className="d-flex align-items-center">
              <Icon name={objectIconName} size={20} className="text-meta mr-2" />
              <div style={{ flex: 1 }}><span className="text-aside">{sectionName} /</span> {objectName} ({objectType})</div>
              <Icon
                name="check_circle"
                size={20}
                className="text-success mx-2"
                style={{ fontSize: 20, transition: 'opacity .15s linear', opacity: includedReleaseItems.includes(uid) ? 1 : 0 }}
              />
              <button type="button" className="btn btn-sm" onClick={() => onItemAdd(uid)}>Add</button>
            </div>
          </Fragment>
        ))}
        <hr />
        {showAllLink}
      </div>
    </>
  );
};

ReleaseContentMenu.propTypes = {
  itemsById: PropTypes.object,
  releaseItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    uid: PropTypes.string,
    objectId: PropTypes.string,
    objectUid: PropTypes.string,
    objectType: PropTypes.string,
    objectName: PropTypes.string,
    objectIconName: PropTypes.string,
  })),
  onItemAdd: PropTypes.func,
};

export default ReleaseContentMenu;
